import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SellerCustomDomainView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerCustomDomain } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type AddCustomDomainInput = {
  storeSlug: string;
  hostname: string;
};

type RemoveCustomDomainInput = {
  storeSlug: string;
};

export function useAddCustomDomainMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: AddCustomDomainInput) =>
      apiJsonClient<SellerCustomDomainView>(endpoints.seller.customDomains, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hostname: input.hostname }),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateSellerCustomDomain(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// Seller confirms they've added the DNS record (POST /confirm) — starts
// verification server-side and writes the fresh status into the query cache.
export function useConfirmCustomDomainMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (_input: { storeSlug: string }) =>
      apiJsonClient<SellerCustomDomainView>(
        endpoints.seller.customDomainsConfirm,
        { method: "POST" },
      ),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        queryKeys.seller.customDomain(variables.storeSlug),
        data,
      );
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

// Forces a live re-check against Cloudflare (POST /refresh) and writes the
// fresh status straight into the query cache.
export function useRefreshCustomDomainMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (_input: { storeSlug: string }) =>
      apiJsonClient<SellerCustomDomainView>(
        endpoints.seller.customDomainsRefresh,
        { method: "POST" },
      ),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        queryKeys.seller.customDomain(variables.storeSlug),
        data,
      );
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

export function useRemoveCustomDomainMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (_input: RemoveCustomDomainInput) =>
      apiJsonClient<void>(endpoints.seller.customDomains, { method: "DELETE" }),
    onSuccess: async (_data, variables) => {
      await invalidateSellerCustomDomain(queryClient, variables.storeSlug);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
